import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { DialogService } from 'primeng/dynamicdialog';
import { providePrimeNG } from 'primeng/config';

import { KeycloakService } from './core/keycloak/keycloak.service';
import { EnvironmentConfig, EnvironmentService } from './core/services/environment.service';
import { MessageService } from 'primeng/api';

import { addTokenInterceptor } from './core/interceptors/add-token.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';

import { routes } from './app.routes';
import { MyPreset } from './mypreset';

import { provideHighcharts } from 'highcharts-angular';

export function initializeAppFactory(
  kc: KeycloakService,
  envService: EnvironmentService,
  http: HttpClient,
): Promise<boolean> {
  return firstValueFrom(http.get<EnvironmentConfig>('/assets/env.json'))
    .then((env) => {
      const isLocalhost = ['localhost', '127.0.0.1'].includes(location.hostname);
      const environment = isLocalhost ? 'local' : env.env_type === 'dev' ? 'development' : 'production';

      envService.config = env;
      kc.setConfig(env);

      Sentry.init({
        dsn: 'https://ea8cefeff03b2900127386244cedf9be@o4511808032276480.ingest.us.sentry.io/4511813547786240',
        enabled: !isLocalhost,
        environment,
      });

      Sentry.setTag('environment', environment);
      return kc.init();
    })
    .catch((err) => {
      console.error('Failed to load env.json', err);
      return Promise.reject(err);
    });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideAppInitializer(() =>
      initializeAppFactory(inject(KeycloakService), inject(EnvironmentService), inject(HttpClient)),
    ),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: false,
          css: {
            ':root': {
              '--font-family': 'Inter, sans-serif',
            },
          },
        },
      },
    }),
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideHttpClient(withInterceptors([addTokenInterceptor, errorInterceptor])),
    MessageService,
    DialogService,
    provideHighcharts({
      instance: () => import('highcharts/esm/highcharts').then((m) => m.default),
      modules: () => {
        return [import('highcharts/esm/modules/stock')];
      },
    }),
  ],
};
